/* eslint-disable no-prototype-builtins */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Paper } from '@esgian/esgianui';
import LogDrawer from '@components/Drawers/LogDrawer/LogDrawer';
import { PeriodicOverviewWithDetailsChart } from '@components/Charts';
import { useDisplayUnit, useSeriesBuilder, useTheme } from '@hooks';
import { useDispatch, useSelector } from 'react-redux';
import {
  getMainPageSlice,
  setChartSelectedDate
} from '@store/features/filters/MainPageSlice/MainPageSlice';
import { getDisplayUnit } from '@store/features';
import { getStackedBarChartMax, getUnixTimeStamp, getValue } from '@helpers';
import moment from 'moment-timezone';
import { API_ROLLUP, DATE_FORMAT, TIMESTAMP, UTC_TIMESTAMP } from '@constants/index';
import { useQuery } from '@tanstack/react-query';
import { useTimezone } from '@hooks/useTimezone';
import { fetchEquipmentData, fetchEquipmentSignalsData } from '@api/Rig';
import { findEquipmentSignal, findEquipmentComponent, hasTimeSeriesValues } from '@api/helpers';
import { DailyRegeneratedEnergyDrawworks, DetailedRegeneratedEnergyDrawworks } from '@components/SubSections/Equipment';
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack
} from '@esgian/esgianui';
import { logError } from '@api/Logging';
import { 
  setSelectedEquipmentName,
  setIsEquipmentSelectedFlag,
  getRigSlice
} from '@store/features/filters/RigSlice/RigSlice';

function PeriodicOverviewDetails({ 
  equipmentData,
  equipmentSignalsData,
  equipmentConfig,
  loading,
  equipmentSignalsLoading }) {
  const dispatch = useDispatch();
  const [ selectedView, handleViewChange ] = useState('hour');
  const [ logInfo, setLogInfo ] = useState(null);
  const { getCanvasTimeSeriesData } = useSeriesBuilder();
  const { getUnitTitle, convertUnitValue } = useDisplayUnit();
  const { theme } = useTheme();
  const {
    palette: { charts, success, error }
  } = theme;
  const displayUnit = useSelector(getDisplayUnit);
  const { selectedRig, chartSelectedDate } = useSelector(getMainPageSlice);
  const { selectedTimeZone, timezoneOffsetString } = useTimezone();
  
  const rigStateSettings = useSelector(state => getRigSlice(state, selectedRig.rigName));
  const selectedEquipmentName = rigStateSettings?.selectedEquipmentName;
  const isEquipmentSelected = rigStateSettings?.isEquipmentSelected;
  
  // Retrieve general Equipment data
  const { primaryUnit, primarySeries, firstChartDate } = useMemo(() => {
    if (!equipmentData || !equipmentConfig?.equipmentDetails) return { primarySeries: [], firstChartDate: null };

    const overviewSeries = [];
    const { componentCalculations } = equipmentData;

    const { equipmentDetails } = equipmentConfig;
    equipmentDetails
      .sort((a, b) => (a.chartConfig?.order ?? Infinity) - (b.chartConfig?.order ?? Infinity))
      .forEach(({ equipmentName, equipmentType, chartConfig }) => {
        const signal = findEquipmentSignal(componentCalculations, equipmentName, displayUnit);
        if (!signal) return;

        const data = signal.timeSeriesValues || {};
        const { defaultArray, convertedArray } = getCanvasTimeSeriesData(data, displayUnit);

        overviewSeries.push({
          label: chartConfig.displayName,
          type: 'bar',
          order: chartConfig.order,
          group: equipmentType,
          borderColor: charts?.bgColour ?? charts[chartConfig.bgColourGroupName][chartConfig.bgColourGroupIndex] ?? '#000000',
          backgroundColor: charts?.bgColour ?? charts[chartConfig.bgColourGroupName][chartConfig.bgColourGroupIndex] ?? '#000000',
          borderSkipped: true,
          stack: 1,
          defaultArray: defaultArray,
          data: [],
          convertedArray: convertedArray
        });
      });

    let maxTot = getStackedBarChartMax(
      overviewSeries?.map(({ defaultArray }) => {
        return { data: defaultArray };
      })
    );

    const { title, converted } = getUnitTitle(maxTot);

    const mappedSeries = overviewSeries
      .filter((series) => series.defaultArray.length > 0 && series.defaultArray.some(d => d.y != null && d.y !== 0))
      .map((series) => {
        series.data = converted ? series.convertedArray : series.defaultArray;
        series.unit = title;
        series.converted = converted;
        return series;
      });

    return {
      primaryUnit: title,
      primarySeries: mappedSeries,
      firstChartDate: mappedSeries[0]?.defaultArray[0]?.x || null
    };
  }, [displayUnit, equipmentData, equipmentConfig]);

  //Retrieve Signal data for each equipment
  const { signalsPrimarySeries } = useMemo(() => {
    if (!equipmentSignalsData)
      return { signalsPrimarySeries: [] };

    const { componentCalculations } = equipmentSignalsData;
    const overviewSignalsSeries = [];

      if(componentCalculations.length > 0) {
        componentCalculations.forEach(calculation => {
          let overviewSeries2Order = 0;
          calculation.signals
            .filter(({metricType}) => metricType === MetricTypes[displayUnit])
            .sort((a, b) => (a.chartConfig?.order ?? Infinity) - (b.chartConfig?.order ?? Infinity))
            .forEach(signal => {
              let chartConfig = signal.chartConfig;
              let displayData = {
                label: chartConfig.displayName === 'Unknown component' ? signal.name : chartConfig.displayName,
                type: 'bar',
                order: chartConfig.order,
                group: '',
                equipmentName: calculation.name,
                metricType: signal.metricType,
                backgroundColor: charts.bgColour ?? charts[chartConfig.bgColourGroupName][chartConfig.bgColourGroupIndex] ?? '#000000',
                borderSkipped: true,
                stack: 1,
                data: []
              };
              let timeSeriesData = signal.timeSeriesValues;
              
              let { defaultArray, convertedArray } = getCanvasTimeSeriesData(timeSeriesData, displayUnit);
              displayData.defaultArray = defaultArray.sort((a, b) => a.x - b.x);
              displayData.convertedArray = convertedArray.sort((a, b) => a.x - b.x);
              overviewSeries2Order += 1;

              overviewSignalsSeries.push(displayData);
            })
        });
      }
      
      let maxTot = getStackedBarChartMax(
        overviewSignalsSeries?.map(({ defaultArray }) => {
          return { data: defaultArray };
        })
      );

      const { title, converted } = getUnitTitle(maxTot);

      return {
        signalsPrimarySeries: overviewSignalsSeries
          .filter((series) => series.defaultArray.length > 0 && series.defaultArray.some(d => d.y != null && d.y !== 0))
          .map((series) => {
            series.data = converted ? series.convertedArray : series.defaultArray;
            series.unit = title;
            series.converted = converted;
            return series;
          })
      }
  }, [displayUnit, equipmentSignalsData]);

  // Use an effect to dispatch setChartSelectedDate
  useEffect(() => {
    if (!chartSelectedDate && firstChartDate) {
      dispatch(
        setChartSelectedDate({
          date: moment.utc(firstChartDate).format(UTC_TIMESTAMP),
          index: 0,
          isLast: false
        })
      );
    }
  }, [chartSelectedDate, firstChartDate, dispatch]);

  const secondaryPrimaryChartData = useMemo(() => {
    if (!equipmentData) return {};
    const { componentCalculations } = equipmentData;
    var reusedData = computeReusedStats(componentCalculations);

    return reusedData;
  }, [equipmentData]);

  const secondaryDataQuery = useQuery({
    queryKey: [
      'equipment',
      {
        selectedView,
        chartSelectedDate,
        selectedRig, 
        selectedEquipmentName,
        equipmentConfig
      }
    ],
    enabled: !!selectedRig && !!selectedView && !!primarySeries && !!equipmentConfig,
    placeholderData: { equipmentData: [], reusedData: {} },
    queryFn: ({ signal }) => {
      if(!primarySeries)
        return { equipmentData: [], reusedData: {} };

      const rollup = selectedView === 'hour' ? API_ROLLUP.HOUR : API_ROLLUP.MIN;

      let sDate = moment(chartSelectedDate.date).startOf('day');
      let eDate = moment(chartSelectedDate.date).endOf('day');

      if (chartSelectedDate.index >= 1 && selectedView === 'hour') {
        sDate = sDate.subtract(1, 'days');
      }
      if (!chartSelectedDate.isLast && selectedView === 'hour') {
        eDate = eDate.add(1, 'days');
      }

      const timezoneMoment = moment.tz(eDate, selectedTimeZone); // Ensure timezone alignment
      const now = moment.tz(selectedTimeZone); // Current time in the same timezone

      if (timezoneMoment.isAfter(now)) {
        eDate = now; // Adjust `eDate` to current time if it's in the future
      }
      const startDate = `${sDate.format(TIMESTAMP)}${timezoneOffsetString}`;
      const endDate = `${eDate.format(TIMESTAMP)}${timezoneOffsetString}`;

      return fetchPowerData(signal, selectedEquipmentName, { selectedRig, startDate, endDate }, rollup)
        .catch((error) => {
          logError(error);
          return { equipmentData: [], reusedData: {} };
        });
    }
  });

  function fetchPowerData(signal, equipmentName, requestData, rollup) {
    if (!isEquipmentSelected)
      return fetchRigPowerData(signal, requestData, rollup);
    else {
      return fetchEquipmentPowerData(signal, equipmentName, requestData, rollup);
    }
  }

  function fetchRigPowerData(signal, requestData, rollup) {
    return fetchEquipmentData(signal, requestData, rollup)
      .then((result) => {
        if (!equipmentConfig.equipmentDetails)
          return { equipmentData: [], reusedData: {} };

        let res = { ...result };
        const { componentCalculations } = res;
        const tempSeries = [];
        const { equipmentDetails } = equipmentConfig;
        const reusedData = computeReusedStats(componentCalculations);
        equipmentDetails
          .sort((a, b) => (a.chartConfig?.order ?? Infinity) - (b.chartConfig?.order ?? Infinity))
          .forEach(({ equipmentName, equipmentType, chartConfig }) => {
              const signal = findEquipmentSignal(componentCalculations, equipmentName, 'Power');
              const data = signal?.timeSeriesValues || {};
              if (!data || Object.keys(data).length === 0) return;
              if (Object.values(data).every((v) => getValue(v, false) === 0 || v == null)) return;
              let seriesData = data
                ? Object.keys(data).map((date) => {
                    return { x: getUnixTimeStamp(date), y: getValue(data[date], false) };
                  })
                : [];

              tempSeries.push({
                label: chartConfig.displayName,
                type: 'line',
                order: chartConfig.order,
                group: equipmentType,
                borderColor: charts?.bgColour ?? charts[chartConfig.bgColourGroupName][chartConfig.bgColourGroupIndex] ?? '#000000',
                data: seriesData
              });
          });
          return {
            equipmentData: tempSeries,
            reusedData: reusedData
          };
      })
  }

  function fetchEquipmentPowerData(signal, equipmentName, requestData, rollup) {
    return fetchEquipmentSignalsData(signal, equipmentName, requestData, rollup)
      .then((result) => {
        let res = { ...result };
        let { signals } = res;
        const overviewSignalsSecondarySeries = [];

        if(signals.length > 0) {
          let overviewSeries2Order = 0;
          signals
            .filter(({metricType}) => metricType === MetricTypes['Power'])
            .sort((a, b) => (a.chartConfig?.order ?? Infinity) - (b.chartConfig?.order ?? Infinity))
            .forEach(eqSignal => {
              let chartConfig = eqSignal.chartConfig;
              let displayData = {
                label: chartConfig.displayName === 'Unknown component' ? eqSignal.name : chartConfig.displayName,
                type: 'line',
                order: chartConfig.order,
                group: '',
                spanGaps: true,
                equipmentName: equipmentName,
                metricType: eqSignal.metricType,
                backgroundColor: chartConfig.bgColour ?? charts[chartConfig.bgColourGroupName][chartConfig.bgColourGroupIndex] ?? '#000000',
                borderColor: chartConfig.bgColour ?? charts[chartConfig.bgColourGroupName][chartConfig.bgColourGroupIndex] ?? '#000000'
              };
              let timeSeriesData = eqSignal.timeSeriesValues;
              if (!hasTimeSeriesValues(timeSeriesData)) return;

              let seriesData = Object.keys(timeSeriesData).map((date) => {
                return { x: getUnixTimeStamp(date), y: timeSeriesData[date] };
              });
              seriesData = seriesData.sort((a, b) => a.x - b.x);
              displayData.data = seriesData;
              displayData.segment = {
                  borderDash: (ctx) => {
                    const d0 = seriesData[ctx.p0DataIndex];
                    const d1 = seriesData[ctx.p1DataIndex];

                    return (d0?.y == null || d1?.y == null) ? [5, 5] : undefined;
                  }
                }
              overviewSignalsSecondarySeries.push(displayData);
              
              overviewSeries2Order += 1;
            });

          return {
            equipmentData: overviewSignalsSecondarySeries
          };
        }
      })
  }

  function computeReusedStats(componentCalculations) {
    const reusedEnergyComponent = findEquipmentComponent(componentCalculations, 'ReusedEnergy');
    const vfdSignal = reusedEnergyComponent.signals.find(signal => signal.name === 'VFD_Reused_Energy');
    const dcBreakSignal = reusedEnergyComponent.signals.find(signal => signal.name === 'DC_Break_Res_Energy');

    const VFD_Reused_Energy = vfdSignal?.timeSeriesValues || {};
    const DC_Break_Res_Energy = dcBreakSignal?.timeSeriesValues || {};

    let reused = { default: [], converted: [] };
    let burned = { default: [], converted: [] };
    let max = 0;

    Object.keys(VFD_Reused_Energy).forEach((key) => {
      let value = getValue(VFD_Reused_Energy[key]);
      max = max < value ? value : max;
      reused.default.push({ x: getUnixTimeStamp(key), y: getValue(value) });
      reused.converted.push({
        x: getUnixTimeStamp(key),
        y: convertUnitValue(value, displayUnit, false)
      });
    });

    Object.keys(DC_Break_Res_Energy).forEach((key) => {
      let value = getValue(DC_Break_Res_Energy[key]);
      max = max < value ? value : max;
      burned.default.push({ x: getUnixTimeStamp(key), y: getValue(value) });
      burned.converted.push({
        x: getUnixTimeStamp(key),
        y: convertUnitValue(value, displayUnit, false)
      });
    });

    if (max === 0) {
      return { unit: '', reused: [], burned: [] };
    }

    const { title, converted } = getUnitTitle(max, displayUnit);
    return {
      unit: title,
      reused: converted ? reused.converted : reused.default,
      burned: converted ? burned.converted : burned.default
    }
  }


  const sortedEquipmentLookup = useMemo(() => {
    let sortedEquipmentsLookup = [{ value: 0, name: 'All' }]
    if(!signalsPrimarySeries?.length || !equipmentConfig?.equipmentDetails) 
      return sortedEquipmentsLookup;

    const { equipmentDetails } = equipmentConfig;
    const orderMap = new Map(equipmentDetails.map(item => [item.equipmentName, item.chartConfig?.order ?? Infinity]))
    
    let signalEquipments = signalsPrimarySeries?.map((equipment) => equipment.equipmentName);
    signalEquipments = [...new Set(signalEquipments)].filter(name => orderMap.has(name));
    signalEquipments = signalEquipments.sort((a, b) => {
      const orderA = orderMap.get(a) ?? Infinity;
      const orderB = orderMap.get(b) ?? Infinity;
      return orderA - orderB;
    });

    signalEquipments
      .forEach((equipment, index) => { sortedEquipmentsLookup.push({ value: index + 1, name: equipment }); });
    
    return sortedEquipmentsLookup;
  }, [signalsPrimarySeries, equipmentConfig]);

  const { sDate, eDate } = useMemo(() => {
    if (!chartSelectedDate?.date) return { sDate: null, eDate: null };
    let sDate = moment(chartSelectedDate?.date).startOf('day');
    let eDate = moment.tz(chartSelectedDate?.date, selectedTimeZone).endOf('day');

    if (chartSelectedDate.index >= 1) {
      sDate = sDate.subtract(1, 'days');
    }
    if (!chartSelectedDate.isLast) {
      eDate = eDate.add(1, 'days');
    }
    if (eDate.isAfter(moment.tz(selectedTimeZone.date))) {
      eDate = moment.tz(selectedTimeZone.date);
    }
    sDate = sDate.format(DATE_FORMAT);
    eDate = eDate.format(DATE_FORMAT);
    return { sDate, eDate };
  }, [chartSelectedDate]);
  
  const selectedEquipmentValue = useMemo(() => {
    if (!selectedEquipmentName) return 0;
    return sortedEquipmentLookup.find(({name}) => name === selectedEquipmentName)?.value ?? 0;
  }, [sortedEquipmentLookup, selectedEquipmentName]);
  
  const handleEquipmentChange = useCallback(
    ({ target }) => {
      let selectedEquipmentLookup = sortedEquipmentLookup?.find(({value}) => value === parseInt(target.value));
      
      dispatch(setSelectedEquipmentName({
        selectedEquipmentName: selectedEquipmentLookup.name, 
        rigName: selectedRig.rigName
      }));

      let selectedEquipmentValue = sortedEquipmentLookup.find(({name}) => name === selectedEquipmentLookup.name)?.value ?? 0;
      if (!selectedEquipmentLookup.name || selectedEquipmentValue === 0) {
        dispatch(setIsEquipmentSelectedFlag({
          isEquipmentSelected: false, 
          rigName: selectedRig.rigName
        }))
        return;
      }

      let newSelectedEquipment = signalsPrimarySeries?.filter(({equipmentName}) => equipmentName === selectedEquipmentLookup.name);
      if (!newSelectedEquipment || newSelectedEquipment.length === 0) {
        dispatch(setIsEquipmentSelectedFlag({
          isEquipmentSelected: false, 
          rigName: selectedRig.rigName
        }))
        return;
      }

      dispatch(setIsEquipmentSelectedFlag({
        isEquipmentSelected: true, 
        rigName: selectedRig.rigName
      }))
    },
    [signalsPrimarySeries, sortedEquipmentLookup]
  );

  return (
    <Paper sx={{ p: 2 }}>
      <PeriodicOverviewWithDetailsChart
        chartToggleActions = 
        {(
          <Stack key={'equipment-select-comp'}>
            <FormControl sx={{ minWidth: 200, height: '20px'}} size={'small'}>
              <InputLabel id='equipment-select-label'>
                {!sortedEquipmentLookup.length ? (
                    <CircularProgress size={20} />
                  ) : 'Equipment'
                }
              </InputLabel>
              <Select
                labelId='equipment-select-label' 
                id='equipment-select'
                value={selectedEquipmentValue}
                label={!sortedEquipmentLookup.length ? (
                    <CircularProgress size={20} />
                  ) : 'Equipment'
                }
                onChange={(loading || equipmentSignalsLoading) ? undefined : handleEquipmentChange}
                >
                {sortedEquipmentLookup.map(({value, name}) => (
                  <MenuItem
                    key={`equipment-select-${value}`}
                    value={value}>
                    {name}
                  </MenuItem>
                ))}
                {(loading || equipmentSignalsLoading) &&
                  <MenuItem
                      key={`equipment-select-${loading}`}
                      value='loading'>
                      <CircularProgress size={20} />
                  </MenuItem>
                }
              </Select>
            </FormControl>
          </Stack>
        )}
        SecondaryPrimaryChart={(!isEquipmentSelected || selectedEquipmentName.toLowerCase() === 'drawworks') ? DailyRegeneratedEnergyDrawworks : undefined}
        secondaryPrimaryChartData={{ ...secondaryPrimaryChartData, setSelectedColumn: (v) => dispatch(setChartSelectedDate(v))}}
        SecondarySubChart={DetailedRegeneratedEnergyDrawworks}
        secondarySubChartData={secondaryDataQuery?.data?.reusedData}
        selectedColumn={{ ...chartSelectedDate, selectedDate: { sDate, eDate } } ?? {}}
        setSelectedColumn={(v) => dispatch(setChartSelectedDate(v))}
        secondarySeries={secondaryDataQuery?.data?.equipmentData}
        secondaryLoading={secondaryDataQuery?.isFetching}
        secondaryUnit={'kW'}
        handleLogClick={setLogInfo}
        detailsTitle={'Equipment details'}
        detailsSubTitleTooltipText={`Total power consumption per hour for each equipment. 
          Based on the day selected in the graph above, 
          it shows the equipment power consumption for the day before, the selected day and the day after. 
          Choose “View by Min” to view the power consumption in more detail`}
        handleViewChange={handleViewChange}
        primaryUnit={primaryUnit}
        primarySeries={isEquipmentSelected ? signalsPrimarySeries.filter(({equipmentName}) => equipmentName === selectedEquipmentName) : primarySeries}
        loading={loading}
        displayZeroTooltipValuesSecondaryChart={true}
        selectedView={selectedView}
        detailsSubTitle="Detailed equipment power consumption in selection"
        title="Equipment overview"
        titleTooltipText={`Total consumption or emissions per day for each equipment in the selected time period.
Clicking on one of the bars will reveal the details per hour for the day before, selected day and the next day in the Equipment details graph below.`}
        id={'equipment-power-detailed-chart'}
        tooltipDatasetMaxHeightPx={300}
        positionSecondaryTooltipAboveChart={true}
        displayReused={!isEquipmentSelected}
      />
      <LogDrawer handleClose={() => setLogInfo(null)} open={!!logInfo} logInfo={logInfo} />
    </Paper>
  );
}

PeriodicOverviewDetails.propTypes = {
  loading: PropTypes.bool,
  equipmentData: PropTypes.object,
  primarySeries: PropTypes.arrayOf(PropTypes.object),
  equipmentConfig: PropTypes.shape({
    rigId: PropTypes.number,
    equipmentDetails: PropTypes.arrayOf(
      PropTypes.shape({
        equipmentName: PropTypes.string,
        equipmentType: PropTypes.string,
        legacyEquipmentName: PropTypes.string,
        chartConfig: PropTypes.shape({
          displayName: PropTypes.string,
          order: PropTypes.number,
          bgColour: PropTypes.string,
          bgColourGroupName: PropTypes.string,
          bgColourGroupIndex: PropTypes.number
        })
      })
    )
  }),
  equipmentSignalsData: PropTypes.shape({
    rigName: PropTypes.string,
    rigId: PropTypes.number,
    componentCalculations: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.string,
        signals: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            rollup: PropTypes.string,
            timeSeriesValues: PropTypes.object, 
            measurementUnit: PropTypes.string,
            metricType: PropTypes.string
          })
        )
      })
    )
  })
};

PeriodicOverviewDetails.defaultProps = {
  loading: false,
  equipmentData: null,
  equipmentSignalsData: null
};

const MetricTypes = Object.freeze({
  Undefined: 'Undefined',
  Power: 'Power',
  Energy: 'Energy',
  Fuel: 'Fuel',
  NOx: 'NOx',
  CO2: 'CO2',
  TotP: 'TotP',
  EnergyCons: 'EnergyCons'
});

export default PeriodicOverviewDetails;
